import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can, rupees, fmtDate } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

const empty = { name: "", mobile: "", email: "", address: "", city: "", kyc_type: "", kyc_number: "", gstin: "", login_email: "", login_username: "", login_password: "", referral_agent_code: "", referral_percent: "" };

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [agents, setAgents] = useState([]);
  const canCreate = can(user, "customers", "create");
  const canSetLogin = can(user, "users", "create");

  const load = useCallback(async () => {
    setRows((await api.get(`/customers${search ? `?search=${search}` : ""}`)).data);
  }, [search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/agents/names").then(({ data }) => setAgents(data)).catch(() => setAgents([])); }, []);

  const submit = async () => {
    try {
      const body = { ...form };
      if (body.referral_percent === "") delete body.referral_percent; else body.referral_percent = Number(body.referral_percent);
      await api.post("/customers", body);
      toast.success("Customer added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Customers / CRM" subtitle="Customer master, KYC & 360° history.">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile…" className="pl-9 w-56 bg-white border-[#E6E2D6]" data-testid="customer-search" />
        </div>
        {canCreate && <Button onClick={() => setOpen(true)} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-customer-btn"><Plus size={16} className="mr-1" /> New Customer</Button>}
      </PageHeader>

      <Card className="bg-white border-[#E6E2D6] shadow-sm">
        {rows.length === 0 ? <EmptyState /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>City</TableHead>
                <TableHead>KYC</TableHead><TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Since</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-row-${c.mobile}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="tabular-nums">{c.mobile}</TableCell>
                  <TableCell className="text-sm">{c.city || "-"}</TableCell>
                  <TableCell className="text-xs">{c.kyc_type ? `${c.kyc_type}` : "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupees(c.outstanding || 0)}</TableCell>
                  <TableCell className="text-sm text-[#8A8A8A]">{fmtDate(c.created_at)}</TableCell>
                  <TableCell><ArrowRight size={16} className="text-[#C5A059]" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#6B6B6B]">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-name" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-mobile" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-email" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-city" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">KYC Type</Label>
              <Select value={form.kyc_type || ""} onValueChange={(v) => setForm({ ...form, kyc_type: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-kyc-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="PAN">PAN</SelectItem><SelectItem value="Aadhaar">Aadhaar</SelectItem><SelectItem value="GST">GST</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">KYC Number</Label><Input value={form.kyc_number} onChange={(e) => setForm({ ...form, kyc_number: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-kyc-number" /></div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-address" /></div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">GSTIN (optional)</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-gstin" /></div>
            <div className="col-span-2 grid grid-cols-2 gap-3 border-t border-[#E6E2D6] pt-3 mt-1">
              <div className="col-span-2 text-xs font-semibold uppercase tracking-wider text-[#8A6D2F]">Referral (optional)</div>
              <div><Label className="text-xs text-[#6B6B6B]">Referral Agent</Label>
                <Select value={form.referral_agent_code || "none"} onValueChange={(v) => setForm({ ...form, referral_agent_code: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-referral-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-56">
                    <SelectItem value="none">No referral</SelectItem>
                    {agents.map((a) => <SelectItem key={a.id} value={a.agent_code}>{a.name} ({a.agent_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-[#6B6B6B]">Referral % (blank = agent default)</Label><Input type="number" value={form.referral_percent} onChange={(e) => setForm({ ...form, referral_percent: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-referral-pct" /></div>
            </div>
            {canSetLogin && (
              <div className="col-span-2 border-t border-[#E6E2D6] pt-3 mt-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#8A6D2F] mb-2">Customer Portal Login (optional)</div>
                <p className="text-[11px] text-[#8A8A8A] mb-2">Set a login so the customer can view their passbook (schemes & purchases). Use email OR username. Leave blank to skip.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs text-[#6B6B6B]">Login Email</Label><Input value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-login-email" /></div>
                  <div><Label className="text-xs text-[#6B6B6B]">Username</Label><Input value={form.login_username} onChange={(e) => setForm({ ...form, login_username: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-login-username" /></div>
                  <div><Label className="text-xs text-[#6B6B6B]">Password</Label><Input type="password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="cf-login-password" /></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={submit} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="cf-save">Add Customer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
